import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, FileText, ExternalLink, Upload, CheckCircle2, XCircle, ClipboardList } from 'lucide-react';
import {
  AssignmentDetail,
  AssignmentFile,
  AssignmentListRow,
  AssignmentSubmissionRow,
  AssignmentsApi,
  CreateAssignmentData,
  GradeSubmissionData,
} from '@/services/assignments.api';
import { AcademicClassesApi, AcademicYearsApi } from '@/services/academic.api';
import { UploadApi } from '@/services/school.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Field, Input, Select, Textarea } from '@/components/ui/Input';
import { Checkbox } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Badge } from '@/components/ui/Badge';
import { usePermissions } from '@/hooks/usePermissions';

function errMsg(e: unknown): string {
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong. Please try again.';
}

const STATUS_LABEL: Record<number, string> = { 0: 'Pending', 1: 'Accepted', 2: 'Rejected', 3: 'Resubmitted' };
function statusTone(status: number): 'amber' | 'green' | 'red' | 'blue' {
  if (status === 1) return 'green';
  if (status === 2) return 'red';
  if (status === 3) return 'blue';
  return 'amber';
}

// ───── Class → Section → Subject cascading picker (same shape as Lessons) ─
function ClassSectionSubjectPicker({
  classId,
  onClassChange,
  sectionId,
  onSectionChange,
  classSubjectId,
  onSubjectChange,
}: {
  classId: string;
  onClassChange: (v: string) => void;
  sectionId: string;
  onSectionChange: (v: string) => void;
  classSubjectId: string;
  onSubjectChange: (v: string) => void;
}) {
  const { data: classesPage } = useQuery({
    queryKey: ['academic-classes', 'all-for-assignments-picker'],
    queryFn: () => AcademicClassesApi.list({ limit: 100 }),
  });
  const classes = classesPage?.items ?? [];
  const sections = classes.find((c) => c.id === classId)?.sections ?? [];

  const { data: subjectRows = [] } = useQuery({
    queryKey: ['class-subjects', classId, 'for-assignments'],
    queryFn: () => AcademicClassesApi.subjects(classId),
    enabled: !!classId,
  });
  // classSubjectId repeats per section/teacher combination — dedupe by
  // subjectId for the picker, same as LessonsPage's own picker.
  const subjects = useMemo(() => {
    const seen = new Map<string, { classSubjectId: string; subjectName: string }>();
    for (const r of subjectRows) if (!seen.has(r.subjectId)) seen.set(r.subjectId, { classSubjectId: r.classSubjectId, subjectName: r.subjectName });
    return [...seen.values()];
  }, [subjectRows]);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Field label="Class" required>
        <Select value={classId} onChange={(e) => onClassChange(e.target.value)}>
          <option value="">Select class</option>
          {classes.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Section" required>
        <Select value={sectionId} onChange={(e) => onSectionChange(e.target.value)} disabled={!classId}>
          <option value="">Select section</option>
          {sections.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </Select>
      </Field>
      <Field label="Subject" required>
        <Select value={classSubjectId} onChange={(e) => onSubjectChange(e.target.value)} disabled={!classId}>
          <option value="">Select subject</option>
          {subjects.map((s) => (
            <option key={s.classSubjectId} value={s.classSubjectId}>
              {s.subjectName}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

// ───── File attach/list widget ────────────────────────────────────────────
function FilesManager({
  files,
  onAdd,
  adding,
  addError,
  onRemove,
  removingId,
}: {
  files: AssignmentFile[];
  onAdd: (data: { fileUrl: string; fileName?: string }) => void;
  adding: boolean;
  addError?: unknown;
  onRemove: (fileId: string) => void;
  removingId?: string;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | undefined>();

  async function handlePick(file: File) {
    setUploading(true);
    setUploadError(undefined);
    try {
      const res = await UploadApi.upload(file);
      onAdd({ fileUrl: res.url, fileName: res.name });
    } catch (e) {
      setUploadError(errMsg(e));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div>
      {files.length === 0 ? (
        <p className="mb-2 text-xs text-slate-400">No files attached yet.</p>
      ) : (
        <ul className="mb-3 divide-y divide-slate-100">
          {files.map((f) => (
            <li key={f.id} className="flex items-center justify-between py-1.5">
              <a href={f.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-sm text-brand-600 hover:underline">
                <FileText className="h-4 w-4 shrink-0" />
                {f.fileName || 'Attachment'}
                <ExternalLink className="h-3 w-3 shrink-0 text-slate-400" />
              </a>
              <button
                type="button"
                title="Remove"
                className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                disabled={removingId === f.id}
                onClick={() => onRemove(f.id)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <label className="btn-secondary flex w-fit cursor-pointer items-center gap-1.5 !py-1 text-xs">
        {uploading ? 'Uploading…' : <><Upload className="h-3.5 w-3.5" /> Attach file</>}
        <input type="file" className="hidden" disabled={uploading || adding} onChange={(e) => e.target.files?.[0] && handlePick(e.target.files[0])} />
      </label>
      {uploadError && <p className="mt-1 text-xs text-red-600">{uploadError}</p>}
      {addError ? <p className="mt-1 text-xs text-red-600">{errMsg(addError)}</p> : null}
    </div>
  );
}

// ───── Submission review ───────────────────────────────────────────────
function SubmissionRow({ submission, onGraded }: { submission: AssignmentSubmissionRow; onGraded: () => void }) {
  const [points, setPoints] = useState(submission.points?.toString() ?? '');
  const [feedback, setFeedback] = useState(submission.feedback ?? '');

  const grade = useMutation({
    mutationFn: (data: GradeSubmissionData) => AssignmentsApi.gradeSubmission(submission.id, data),
    onSuccess: onGraded,
  });

  const canDecide = submission.status === 0 || submission.status === 3;

  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-slate-800">{submission.studentName ?? '—'}</p>
          <p className="text-xs text-slate-500">{submission.admissionNo}</p>
        </div>
        <Badge tone={statusTone(submission.status)}>{STATUS_LABEL[submission.status] ?? submission.status}</Badge>
      </div>

      {submission.files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {submission.files.map((f) => (
            <li key={f.id}>
              <a href={f.fileUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-xs text-brand-600 hover:underline">
                <FileText className="h-3 w-3" /> {f.fileName || 'Submitted file'}
              </a>
            </li>
          ))}
        </ul>
      )}

      {canDecide ? (
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <Field label="Points" className="!mb-0 w-24">
            <Input type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
          </Field>
          <Field label="Feedback" className="!mb-0 flex-1">
            <Input value={feedback} onChange={(e) => setFeedback(e.target.value)} />
          </Field>
          <button
            type="button"
            className="btn-primary flex items-center gap-1 !py-1.5 text-xs"
            disabled={grade.isPending}
            onClick={() => grade.mutate({ status: 'accept', points: points ? Number(points) : undefined, feedback: feedback || undefined })}
          >
            <CheckCircle2 className="h-3.5 w-3.5" /> Accept
          </button>
          <button
            type="button"
            className="btn-secondary flex items-center gap-1 !py-1.5 text-xs text-red-700"
            disabled={grade.isPending}
            onClick={() => grade.mutate({ status: 'reject', feedback: feedback || undefined })}
          >
            <XCircle className="h-3.5 w-3.5" /> Reject
          </button>
        </div>
      ) : (
        submission.feedback && <p className="mt-2 text-xs text-slate-600">Feedback: {submission.feedback}</p>
      )}
      {grade.error && <p className="mt-1 text-xs text-red-600">{errMsg(grade.error)}</p>}
    </div>
  );
}

function SubmissionsPanel({ assignmentId }: { assignmentId: string }) {
  const qc = useQueryClient();
  const { data: submissions = [], isLoading } = useQuery({
    queryKey: ['assignment-submissions', assignmentId],
    queryFn: () => AssignmentsApi.submissions(assignmentId),
  });
  const { data: report } = useQuery({
    queryKey: ['assignment-report', assignmentId],
    queryFn: () => AssignmentsApi.report(assignmentId),
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['assignment-submissions', assignmentId] });
    qc.invalidateQueries({ queryKey: ['assignment-report', assignmentId] });
  };

  if (isLoading) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-3">
      {report && (
        <div className="flex flex-wrap gap-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-600">
          <span>Total: <strong>{report.total}</strong></span>
          <span>Submitted: <strong>{report.submitted}</strong></span>
          <span>Unsubmitted: <strong>{report.unsubmitted}</strong></span>
          <span>Points: <strong>{report.obtainedPoints}/{report.totalPoints}</strong></span>
          <span>%: <strong>{report.percentage}%</strong></span>
        </div>
      )}
      {submissions.length === 0 ? (
        <p className="text-xs text-slate-400">No submissions yet.</p>
      ) : (
        <div className="space-y-2">
          {submissions.map((s) => (
            <SubmissionRow key={s.id} submission={s} onGraded={invalidate} />
          ))}
        </div>
      )}
    </div>
  );
}

// ───── Assignment detail modal (files + submissions) ─────────────────────
function AssignmentDetailView({ assignmentId }: { assignmentId: string }) {
  const qc = useQueryClient();
  const { data: detail } = useQuery({
    queryKey: ['assignments', assignmentId, 'detail'],
    queryFn: () => AssignmentsApi.get(assignmentId),
  });

  const addFile = useMutation({
    mutationFn: (data: { fileUrl: string; fileName?: string }) => AssignmentsApi.addFile(assignmentId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments', assignmentId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
    },
  });
  const [removingId, setRemovingId] = useState<string | undefined>();
  const removeFile = useMutation({
    mutationFn: (fileId: string) => AssignmentsApi.removeFile(assignmentId, fileId),
    onMutate: (fileId) => setRemovingId(fileId),
    onSettled: () => setRemovingId(undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments', assignmentId, 'detail'] });
      qc.invalidateQueries({ queryKey: ['assignments'] });
    },
  });

  if (!detail) return <p className="text-sm text-slate-500">Loading…</p>;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
        <Badge tone="blue">{detail.className}</Badge>
        <Badge tone="slate">{detail.sectionName}</Badge>
        <Badge tone="indigo">{detail.subjectName}</Badge>
        <span>Due {new Date(detail.dueDate).toLocaleString()}</span>
        {detail.points !== null && <span>{detail.points} points</span>}
        {detail.createdByName && <span>Created by {detail.createdByName}</span>}
      </div>
      {detail.instructions && <p className="text-sm text-slate-700">{detail.instructions}</p>}

      <div>
        <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Reference Files</h4>
        <FilesManager
          files={detail.files}
          onAdd={(d) => addFile.mutate(d)}
          adding={addFile.isPending}
          addError={addFile.error}
          onRemove={(id) => removeFile.mutate(id)}
          removingId={removingId}
        />
      </div>

      <div>
        <h4 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <ClipboardList className="h-3.5 w-3.5" /> Submissions
        </h4>
        <SubmissionsPanel assignmentId={assignmentId} />
      </div>
    </div>
  );
}

// ───── Create/Edit form ────────────────────────────────────────────────
function AssignmentForm({ assignment, onDone }: { assignment?: AssignmentListRow; onDone: () => void }) {
  const qc = useQueryClient();
  const [classId, setClassId] = useState('');
  const [sectionId, setSectionId] = useState(assignment?.sectionId ?? '');
  const [classSubjectId, setClassSubjectId] = useState(assignment?.classSubjectId ?? '');
  const [name, setName] = useState(assignment?.name ?? '');
  const [instructions, setInstructions] = useState(assignment?.instructions ?? '');
  const [dueDate, setDueDate] = useState(assignment?.dueDate ? assignment.dueDate.slice(0, 16) : '');
  const [points, setPoints] = useState(assignment?.points?.toString() ?? '');
  const [resubmission, setResubmission] = useState(assignment?.resubmission ?? false);
  const [extraDays, setExtraDays] = useState(assignment?.extraDaysForResubmission?.toString() ?? '');

  useEffect(() => {
    if (!assignment) {
      setSectionId('');
      setClassSubjectId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);

  const payload = (): CreateAssignmentData => ({
    sectionId,
    classSubjectId,
    name,
    instructions: instructions || undefined,
    dueDate: dueDate ? new Date(dueDate).toISOString() : '',
    points: points ? Number(points) : undefined,
    resubmission,
    extraDaysForResubmission: extraDays ? Number(extraDays) : undefined,
  });

  const save = useMutation({
    mutationFn: () => (assignment ? AssignmentsApi.update(assignment.id, payload()) : AssignmentsApi.create(payload())),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['assignments'] });
      onDone();
    },
  });

  const valid = !!sectionId && !!classSubjectId && !!name.trim() && !!dueDate;

  return (
    <div>
      <ClassSectionSubjectPicker
        classId={classId}
        onClassChange={setClassId}
        sectionId={sectionId}
        onSectionChange={setSectionId}
        classSubjectId={classSubjectId}
        onSubjectChange={setClassSubjectId}
      />
      <Field label="Assignment name" required className="mt-3">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Chapter 3 Worksheet" />
      </Field>
      <Field label="Instructions" className="mt-3">
        <Textarea value={instructions} onChange={(e) => setInstructions(e.target.value)} rows={3} />
      </Field>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Due date" required>
          <Input type="datetime-local" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
        </Field>
        <Field label="Points">
          <Input type="number" value={points} onChange={(e) => setPoints(e.target.value)} />
        </Field>
        <Field label="Extra days for resubmission" hint={resubmission ? undefined : 'Enable resubmission below first'}>
          <Input type="number" disabled={!resubmission} value={extraDays} onChange={(e) => setExtraDays(e.target.value)} />
        </Field>
      </div>
      <div className="mt-3">
        <Checkbox label="Allow resubmission after rejection" checked={resubmission} onChange={(e) => setResubmission(e.target.checked)} />
      </div>
      {save.error && <p className="mt-2 text-xs text-red-600">{errMsg(save.error)}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button className="btn-secondary" onClick={onDone}>
          Cancel
        </button>
        <button className="btn-primary" disabled={!valid || save.isPending} onClick={() => save.mutate()}>
          {save.isPending ? 'Saving…' : assignment ? 'Save' : 'Create Assignment'}
        </button>
      </div>
    </div>
  );
}

// ───── Page ────────────────────────────────────────────────────────────
export function AssignmentsPage() {
  const { can } = usePermissions();
  const canWrite = can('/assignments', 'create');
  const canDelete = can('/assignments', 'delete');

  const qc = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<AssignmentListRow | null>(null);
  const [managingId, setManagingId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AssignmentListRow | null>(null);

  // Defaults to the current academic year — without this, the list showed
  // every assignment ever created (including past years'), which read as
  // "stale" at a glance. 'all' aggregates every year, same convention as
  // FeesPage's year selector.
  const [yearId, setYearId] = useState('');
  const { data: currentYear } = useQuery({ queryKey: ['academic-years', 'current'], queryFn: AcademicYearsApi.current });
  const { data: yearsPage } = useQuery({
    queryKey: ['academic-years', 'all-for-assignments-selector'],
    queryFn: () => AcademicYearsApi.list({ limit: 100, sortBy: 'start_date', sortOrder: 'desc' }),
  });
  const years = yearsPage?.items ?? [];
  const effectiveYearId = yearId || currentYear?.id || '';

  const { data: assignments = [], isFetching, error } = useQuery({
    queryKey: ['assignments', effectiveYearId],
    queryFn: () => AssignmentsApi.list({ academicYearId: effectiveYearId === 'all' ? undefined : effectiveYearId || undefined }),
  });

  const remove = useMutation({
    mutationFn: (id: string) => AssignmentsApi.remove(id),
    onSuccess: () => {
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['assignments'] });
    },
  });

  return (
    <div>
      <PageHeader
        title="Assignments"
        description="Create assignments for a class/section/subject, attach reference files, and review student submissions."
        actions={
          canWrite ? (
            <button className="btn-primary flex items-center gap-1.5" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" /> Create Assignment
            </button>
          ) : undefined
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Select className="!w-44" value={yearId || effectiveYearId} onChange={(e) => setYearId(e.target.value)}>
          <option value="all">All Years</option>
          {years.map((y) => (
            <option key={y.id} value={y.id}>
              {y.name}
            </option>
          ))}
        </Select>
      </div>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{errMsg(error)}</div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Assignment</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Class</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Subject</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Due</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Submissions</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {isFetching && !assignments.length ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                      Loading…
                    </td>
                  </tr>
                ) : assignments.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-12 text-center text-slate-400">
                      No assignments created yet.
                    </td>
                  </tr>
                ) : (
                  assignments.map((a) => (
                    <tr key={a.id} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <button type="button" className="font-medium text-brand-600 hover:underline" onClick={() => setManagingId(a.id)}>
                          {a.name}
                        </button>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">{a.className} · {a.sectionName}</td>
                      <td className="px-4 py-2.5 text-slate-700">{a.subjectName}</td>
                      <td className="px-4 py-2.5 text-slate-600">{new Date(a.dueDate).toLocaleDateString()}</td>
                      <td className="px-4 py-2.5 text-right">
                        <Badge tone="slate">{a.submissionCount}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <span className="inline-flex items-center gap-2">
                          {canWrite && (
                            <button type="button" title="Edit" className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-brand-700" onClick={() => setEditing(a)}>
                              <Pencil className="h-4 w-4" />
                            </button>
                          )}
                          {canDelete && (
                            <button type="button" title="Delete" className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600" onClick={() => setDeleting(a)}>
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal open={creating} onClose={() => setCreating(false)} title="Create Assignment" size="lg">
        <AssignmentForm onDone={() => setCreating(false)} />
      </Modal>

      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit Assignment" size="lg">
        {editing && <AssignmentForm assignment={editing} onDone={() => setEditing(null)} />}
      </Modal>

      <Modal
        open={!!managingId}
        onClose={() => setManagingId(null)}
        title={assignments.find((a) => a.id === managingId)?.name ?? 'Assignment'}
        size="lg"
      >
        {managingId && <AssignmentDetailView assignmentId={managingId} />}
      </Modal>

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(null)}
        onConfirm={() => remove.mutate(deleting!.id)}
        title="Delete assignment?"
        message={
          remove.error
            ? errMsg(remove.error)
            : `This permanently deletes "${deleting?.name}" and its attached files. Blocked if any student submissions exist.`
        }
        confirmText="Delete"
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}
